import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import Button from './Button';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: ${props => props.theme.colors.background.card};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${props => props.theme.colors.border};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.lg};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.muted};
  cursor: pointer;
  padding: 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.background.light};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.primary};
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background.main};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.md};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background.main};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.md};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Checkbox = styled.input`
  margin-right: ${props => props.theme.spacing.sm};
`;

const CheckboxLabel = styled.label`
  color: ${props => props.theme.colors.text.primary};
  font-weight: 600;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  background: rgba(255, 94, 91, 0.1);
  border: 1px solid ${props => props.theme.colors.status.error};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.status.error};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSize.sm};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
`;

const WithdrawModal = ({ 
  isOpen, 
  onClose, 
  onWithdraw, 
  userWallet, 
  networkId,
  loading = false,
  error = null 
}) => {
  const [withdrawAll, setWithdrawAll] = useState(false);
  const [tokenSymbol, setTokenSymbol] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [formError, setFormError] = useState('');

  // Available tokens based on network
  const getAvailableTokens = () => {
    const tokens = ['USDT'];
    
    if (networkId === '56' || networkId === '97') {
      tokens.push('BNB');
      tokens.push('GTAI');
    } else {
      tokens.push('ETH');
    }
    
    return tokens;
  };

  const availableTokens = getAvailableTokens();

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setWithdrawAll(false);
      setTokenSymbol('USDT');
      setAmount('');
      setFormError('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!withdrawAll) {
      if (!tokenSymbol) {
        setFormError('Please select a token');
        return;
      }
      
      if (!amount || parseFloat(amount) <= 0) {
        setFormError('Please enter a valid amount');
        return;
      }

      // Check if user has enough balance
      const balance = getTokenBalance(tokenSymbol);
      if (parseFloat(amount) > balance) {
        setFormError(`Insufficient balance. Available: ${balance} ${tokenSymbol}`);
        return;
      }
    }

    onWithdraw({
      withdrawAll,
      tokenSymbol: withdrawAll ? null : tokenSymbol,
      amount: withdrawAll ? null : parseFloat(amount)
    });
  };

  const getTokenBalance = (symbol) => {
    if (!userWallet) return 0;
    
    switch (symbol) {
      case 'USDT':
        return userWallet.balance_usdt || 0;
      case 'BNB':
        return userWallet.balance_bnb || 0;
      case 'GTAI':
        return userWallet.balance_gtai || 0;
      default:
        return 0;
    }
  };

  const hasAnyBalance = () => {
    if (!userWallet) return false;
    return (userWallet.balance_usdt > 0) || 
           (userWallet.balance_bnb > 0) || 
           (userWallet.balance_gtai > 0);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Withdraw from Trading Wallet</ModalTitle>
              <CloseButton onClick={onClose}>
                <FaTimes size={18} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <CheckboxWrapper>
                <Checkbox
                  type="checkbox"
                  id="withdrawAll"
                  checked={withdrawAll}
                  onChange={(e) => setWithdrawAll(e.target.checked)}
                  disabled={loading}
                />
                <CheckboxLabel htmlFor="withdrawAll">
                  Withdraw all tokens
                </CheckboxLabel>
              </CheckboxWrapper>

              {!withdrawAll && (
                <>
                  <FormGroup>
                    <Label htmlFor="tokenSymbol">Token</Label>
                    <Select
                      id="tokenSymbol"
                      value={tokenSymbol}
                      onChange={(e) => setTokenSymbol(e.target.value)}
                      disabled={loading}
                    >
                      {availableTokens.map(token => (
                        <option key={token} value={token}>
                          {token} (Balance: {getTokenBalance(token).toFixed(6)})
                        </option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="amount">Amount</Label>
                    <Input
                      type="number"
                      id="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount to withdraw"
                      step="0.000001"
                      min="0"
                      disabled={loading}
                    />
                  </FormGroup>
                </>
              )}

              {(formError || error) && (
                <ErrorMessage>
                  <FaExclamationTriangle />
                  {formError || error}
                </ErrorMessage>
              )}

              {!hasAnyBalance() && (
                <ErrorMessage>
                  <FaExclamationTriangle />
                  No tokens available to withdraw
                </ErrorMessage>
              )}

              <ButtonGroup>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !hasAnyBalance()}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Processing...' : 'Withdraw'}
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default WithdrawModal; 